import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import commissionService from '../../services/ShipmentCommissionService.js';
import { Users, DollarSign, FileText, Eye, X, TrendingUp, Calendar, CreditCard, Download, ChevronDown, ChevronRight, Filter, MapPin } from 'lucide-react';
import { PageLayout, Button, StatusBadge } from '../common/index.js';

const CommissionDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [filteredCommissions, setFilteredCommissions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedPanelRow, setExpandedPanelRow] = useState(null);
  const [totals, setTotals] = useState({
    totalCommission: 0, totalCompanyCommission: 0, totalOwnerCommission: 0,
    totalHubCommission: 0, totalPaidCompany: 0, totalPaidOwner: 0,
    totalPaidHub: 0, totalPendingCompany: 0, totalPendingOwner: 0, totalPendingHub: 0,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const [dateRange, setDateRange] = useState(null);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const dateDropdownRef = useRef(null);

  const filterOptions = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'all', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const getHubTotals = (comm) => {
    const s = comm?.sender_hub_commissions || [];
    const r = comm?.receiver_hub_commissions || [];
    const all = [...s, ...r];
    return { totalHubCommission: all.reduce((a, h) => a + parseFloat(h.hub_commission_amount || 0), 0), totalHubPaid: all.reduce((a, h) => a + parseFloat(h.paid_amount || 0), 0), senderHubs: s, receiverHubs: r };
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

  const getStatusText = (paid, total) => {
    if (paid >= total && total > 0) return 'Paid';
    if (paid > 0) return 'Partial';
    return 'Pending';
  };

  const getStatusBadge = (status) => {
    if (status === 'Paid') return 'bg-green-100 text-green-700';
    if (status === 'Partial') return 'bg-blue-100 text-blue-700';
    return 'bg-red-100 text-red-700';
  };

  const calculateTotals = useCallback((data) => {
    const result = data.reduce((acc, comm) => {
      const { totalHubCommission, totalHubPaid } = getHubTotals(comm);
      acc.totalCommission += parseFloat(comm.company_commission_amount || 0) + parseFloat(comm.vehicle_owner_commission_amount || 0) + totalHubCommission;
      acc.totalCompanyCommission += parseFloat(comm.company_commission_amount || 0);
      acc.totalOwnerCommission += parseFloat(comm.vehicle_owner_commission_amount || 0);
      acc.totalHubCommission += totalHubCommission;
      acc.totalPaidCompany += parseFloat(comm.company_paid_amount || 0);
      acc.totalPaidOwner += parseFloat(comm.vehicle_owner_paid_amount || 0);
      acc.totalPaidHub += totalHubPaid;
      acc.totalPendingCompany += parseFloat(comm.company_commission_amount || 0) - parseFloat(comm.company_paid_amount || 0);
      acc.totalPendingOwner += parseFloat(comm.vehicle_owner_commission_amount || 0) - parseFloat(comm.vehicle_owner_paid_amount || 0);
      acc.totalPendingHub += totalHubCommission - totalHubPaid;
      return acc;
    }, { totalCommission: 0, totalCompanyCommission: 0, totalOwnerCommission: 0, totalHubCommission: 0, totalPaidCompany: 0, totalPaidOwner: 0, totalPaidHub: 0, totalPendingCompany: 0, totalPendingOwner: 0, totalPendingHub: 0 });
    setTotals(result);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await commissionService.getAllCommissions(1, 500, { all: 'true' });
      const data = res.data.data || res.data || [];
      setCommissions(data);
      setFilteredCommissions(data);
      calculateTotals(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [calculateTotals]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (e) => { if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) setIsDateDropdownOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calculateDateRange = (filter, from, to) => {
    const today = new Date();
    let s, e;
    switch (filter) {
      case 'current_month': s = new Date(today.getFullYear(), today.getMonth(), 1); e = new Date(today.getFullYear(), today.getMonth() + 1, 0); break;
      case 'last_3_months': s = new Date(today.getFullYear(), today.getMonth() - 3, 1); e = today; break;
      case 'last_6_months': s = new Date(today.getFullYear(), today.getMonth() - 6, 1); e = today; break;
      case 'last_year': s = new Date(today.getFullYear() - 1, today.getMonth(), 1); e = today; break;
      case 'custom': if (from && to) { s = new Date(from); e = new Date(to); } break;
      default: return null;
    }
    if (s && e) return { start_date: s.toISOString().split('T')[0], end_date: e.toISOString().split('T')[0], days: Math.ceil((e - s) / 86400000) + 1 };
    return null;
  };

  useEffect(() => {
    let filtered = [...commissions];
    if (dateFilter !== 'all') {
      const range = calculateDateRange(dateFilter, customDates.from_date, customDates.to_date);
      if (range) { setDateRange(range); filtered = filtered.filter(c => { const d = new Date(c.created_at).toISOString().split('T')[0]; return d >= range.start_date && d <= range.end_date; }); }
    } else { setDateRange(null); }
    setFilteredCommissions(filtered);
    calculateTotals(filtered);
    setSelectedItem(null);
    setExpandedPanelRow(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commissions, dateFilter, customDates]);

  const getDateFilterLabel = () => filterOptions.find(o => o.value === dateFilter)?.label || 'Select Period';
  const handleDateFilterChange = (v) => { setDateFilter(v); if (v !== 'custom') { setIsDateDropdownOpen(false); setCustomDates({ from_date: '', to_date: '' }); } };
  const handleCustomApply = () => { if (customDates.from_date && customDates.to_date) { setDateFilter('custom'); setIsDateDropdownOpen(false); } };
  const clearFilters = () => { setDateFilter('all'); setCustomDates({ from_date: '', to_date: '' }); setDateRange(null); };

  const downloadExcelReport = async () => {
    try {
      setDownloading(true);
      const params = { date_filter: dateFilter };
      if (dateFilter === 'custom' && customDates.from_date && customDates.to_date) { params.from_date = customDates.from_date; params.to_date = customDates.to_date; }
      const response = await commissionService.downloadCommissionReport(params);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `Commission_Report_${dateFilter}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch (error) { console.error('Error downloading report:', error); alert('Failed to download report.'); } finally { setDownloading(false); }
  };

  const handleTierClick = (type) => {
    setSelectedItem(type);
    setExpandedPanelRow(null);
  };

  if (loading) {
    return (
      <PageLayout title="Commission Dashboard" icon={DollarSign}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading commission data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Build breakdown data for the info panel
  const renderInfoPanel = () => {
    if (!selectedItem) return (
      <div className="p-8 text-center">
        <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center"><FileText className="w-8 h-8 text-gray-400" /></div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Selection</h3>
        <p className="text-sm text-gray-600">Click on any commission tier to view detailed breakdown</p>
      </div>
    );

    const tierConfig = {
      owner: { label: 'Vehicle Owner Commission', gradient: 'from-blue-500 to-blue-600', icon: <Users className="w-6 h-6" />, totalComm: totals.totalOwnerCommission, totalPaid: totals.totalPaidOwner, totalPending: totals.totalPendingOwner },
      company: { label: 'Primary Agent Commission', gradient: 'from-green-500 to-green-600', icon: <Users className="w-6 h-6" />, totalComm: totals.totalCompanyCommission, totalPaid: totals.totalPaidCompany, totalPending: totals.totalPendingCompany },
      hub: { label: 'Location Hub Commission', gradient: 'from-purple-500 to-purple-600', icon: <MapPin className="w-6 h-6" />, totalComm: totals.totalHubCommission, totalPaid: totals.totalPaidHub, totalPending: totals.totalPendingHub },
    };

    const cfg = tierConfig[selectedItem];
    const status = getStatusText(cfg.totalPaid, cfg.totalComm);

    return (
      <div>
        <div className={`p-4 rounded-t-lg text-white bg-gradient-to-r ${cfg.gradient}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {cfg.icon}
              <div><h3 className="font-bold text-lg">{cfg.label}</h3><p className="text-sm opacity-90">{filteredCommissions.length} commission record{filteredCommissions.length !== 1 ? 's' : ''}</p></div>
            </div>
            <button onClick={() => setSelectedItem(null)} className="hover:bg-white/20 rounded-full p-1"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Financial Summary */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Financial Summary</h4>
            <div className="space-y-2">
              <div className="bg-brand-light rounded-lg p-3 flex items-center justify-between"><div><span className="text-xs text-gray-600">Total Commission</span><p className="text-lg font-bold text-brand">{formatCurrency(cfg.totalComm)}</p></div><DollarSign className="w-5 h-5 text-brand" /></div>
              <div className="bg-green-50 rounded-lg p-3 flex items-center justify-between"><div><span className="text-xs text-gray-600">Paid Amount</span><p className="text-lg font-bold text-green-600">{formatCurrency(cfg.totalPaid)}</p></div><TrendingUp className="w-5 h-5 text-green-600" /></div>
              <div className="bg-red-50 rounded-lg p-3 flex items-center justify-between"><div><span className="text-xs text-gray-600">Pending Amount</span><p className="text-lg font-bold text-red-600">{formatCurrency(cfg.totalPending)}</p></div><CreditCard className="w-5 h-5 text-red-600" /></div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <span className="text-sm text-gray-600">Status:</span>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(status)}`}>{status}</span>
          </div>

          {/* Per-commission breakdown */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Breakdown by Commission</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredCommissions.map((comm) => {
                const isExp = expandedPanelRow === comm.commission_id;
                let commAmt = 0, paidAmt = 0;

                if (selectedItem === 'owner') {
                  commAmt = parseFloat(comm.vehicle_owner_commission_amount || 0);
                  paidAmt = parseFloat(comm.vehicle_owner_paid_amount || 0);
                } else if (selectedItem === 'company') {
                  commAmt = parseFloat(comm.company_commission_amount || 0);
                  paidAmt = parseFloat(comm.company_paid_amount || 0);
                } else {
                  const { totalHubCommission, totalHubPaid } = getHubTotals(comm);
                  commAmt = totalHubCommission;
                  paidAmt = totalHubPaid;
                }

                if (commAmt === 0 && paidAmt === 0) return null;

                const rowStatus = getStatusText(paidAmt, commAmt);

                return (
                  <div key={comm.commission_id} className="bg-gray-50 rounded-lg overflow-hidden">
                    <div className="p-3 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setExpandedPanelRow(isExp ? null : comm.commission_id)}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {isExp ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
                          <span className="text-xs font-semibold text-gray-700">{comm.commission_id}</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadge(rowStatus)}`}>{rowStatus}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs ml-5">
                        <span className="text-gray-500">{comm.ml_sheet_id}</span>
                        <div className="flex gap-3">
                          <span className="font-semibold text-brand">{formatCurrency(commAmt)}</span>
                          <span className="font-semibold text-green-600">{formatCurrency(paidAmt)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExp && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-200">
                        {selectedItem === 'owner' && (
                          <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Commission %</p><p className="font-bold">{parseFloat(comm.vehicle_owner_commission_percent || 0).toFixed(2)}%</p></div>
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Total Freight</p><p className="font-bold">{formatCurrency(comm.total_freight)}</p></div>
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Balance</p><p className="font-bold text-red-600">{formatCurrency(commAmt - paidAmt)}</p></div>
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Owner ID</p><p className="font-bold truncate">{comm.vehicle_owner_id}</p></div>
                          </div>
                        )}

                        {selectedItem === 'company' && (
                          <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Commission %</p><p className="font-bold">{parseFloat(comm.company_commission_percent || 0).toFixed(2)}%</p></div>
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Total Freight</p><p className="font-bold">{formatCurrency(comm.total_freight)}</p></div>
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Balance</p><p className="font-bold text-red-600">{formatCurrency(commAmt - paidAmt)}</p></div>
                            <div className="bg-white rounded p-2"><p className="text-gray-500">Company ID</p><p className="font-bold truncate">{comm.company_profile_id}</p></div>
                          </div>
                        )}

                        {selectedItem === 'hub' && (() => {
                          const sHubs = comm.sender_hub_commissions || [];
                          const rHubs = comm.receiver_hub_commissions || [];
                          return (
                            <div className="space-y-3 mt-2">
                              {sHubs.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-purple-700 mb-1">Sender Hubs</p>
                                  {sHubs.map(h => (
                                    <div key={h.hub_id} className="bg-white rounded p-2 mb-1 flex items-center justify-between text-xs">
                                      <div>
                                        <span className="font-semibold">{h.hub_location}</span>
                                        <span className="text-gray-400 ml-1">({h.hub_id})</span>
                                        <span className="text-gray-400 ml-2">{parseFloat(h.hub_commission_percent || 0).toFixed(1)}%</span>
                                      </div>
                                      <div className="flex gap-3">
                                        <span className="font-bold text-brand">{formatCurrency(h.hub_commission_amount)}</span>
                                        <span className="font-bold text-green-600">{formatCurrency(h.paid_amount)}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${h.payment_status === 'PAID' ? 'bg-green-100 text-green-700' : h.payment_status === 'PARTIAL' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{h.payment_status || 'PENDING'}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {rHubs.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-indigo-700 mb-1">Receiver Hubs</p>
                                  {rHubs.map(h => (
                                    <div key={h.hub_id} className="bg-white rounded p-2 mb-1 flex items-center justify-between text-xs">
                                      <div>
                                        <span className="font-semibold">{h.hub_location}</span>
                                        <span className="text-gray-400 ml-1">({h.hub_id})</span>
                                        <span className="text-gray-400 ml-2">{parseFloat(h.hub_commission_percent || 0).toFixed(1)}%</span>
                                      </div>
                                      <div className="flex gap-3">
                                        <span className="font-bold text-brand">{formatCurrency(h.hub_commission_amount)}</span>
                                        <span className="font-bold text-green-600">{formatCurrency(h.paid_amount)}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${h.payment_status === 'PAID' ? 'bg-green-100 text-green-700' : h.payment_status === 'PARTIAL' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{h.payment_status || 'PENDING'}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {sHubs.length === 0 && rHubs.length === 0 && <p className="text-xs text-gray-400">No hub commissions</p>}
                            </div>
                          );
                        })()}

                        <button onClick={() => navigate(`/commissions/view/${comm.commission_id}`)}
                          className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-brand hover:text-brand py-1.5 bg-brand-light rounded font-medium">
                          <Eye className="w-3 h-3" /> View Full Details
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      title="Commission Dashboard"
      icon={DollarSign}
      maxWidth="max-w-[1400px]"
      actions={
        <>
          <Button variant="secondary" icon={MapPin} onClick={() => navigate('/commissions/hub-insights')}>
            Hub Insights
          </Button>
          <Button icon={Filter} onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </>
      }
    >
      <p className="text-gray-600 mb-6 -mt-2">Click on any tier to view detailed breakdown</p>

                {/* Filters */}
                {showFilters && (
                  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                        <div className="relative" ref={dateDropdownRef}>
                          <button onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                            className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 text-sm">
                            <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>{getDateFilterLabel()}</span></div>
                            <ChevronDown className={`w-4 h-4 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {isDateDropdownOpen && (
                            <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-sm shadow-lg z-50">
                              <div className="p-4">
                                <div className="space-y-1 mb-4">
                                  {filterOptions.map(o => (
                                    <button key={o.value} onClick={() => handleDateFilterChange(o.value)}
                                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${dateFilter === o.value ? 'bg-brand-light text-brand font-medium' : 'hover:bg-gray-50 text-gray-700'}`}>{o.label}</button>
                                  ))}
                                </div>
                                {dateFilter === 'custom' && (
                                  <div className="border-t border-gray-200 pt-4">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
                                    <input type="date" value={customDates.from_date} onChange={(e) => setCustomDates({ ...customDates, from_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3" />
                                    <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
                                    <input type="date" value={customDates.to_date} onChange={(e) => setCustomDates({ ...customDates, to_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3" />
                                    <button onClick={handleCustomApply} disabled={!customDates.from_date || !customDates.to_date} className="w-full px-4 py-2 bg-brand text-white rounded text-sm font-medium hover:bg-brand-hover disabled:opacity-50">Apply</button>
                                  </div>
                                )}
                                {dateRange && (
                                  <div className="mt-4 pt-4 border-t border-gray-200">
                                    <p className="text-xs text-gray-600"><span className="font-medium">Period:</span> {new Date(dateRange.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} - {new Date(dateRange.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} <span className="text-gray-500">({dateRange.days} days)</span></p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-0">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Actions</label>
                        <div className="flex gap-4">
                          <button onClick={downloadExcelReport} disabled={downloading || filteredCommissions.length === 0}
                            className="flex items-center w-[65%] justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50"><Download className="w-4 h-4" /> {downloading ? 'Downloading...' : 'Download Report'}</button>
                          <button onClick={clearFilters} className="px-4 py-2 w-[35%] bg-gray-200 text-gray-700 rounded-sm hover:bg-gray-300">Clear Filters</button>
                        </div>
                      </div>
                    </div>
                    {dateFilter !== 'all' && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-brand rounded-full text-xs">{getDateFilterLabel()}</span>
                      </div>
                    )}
                  </div>
                )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                  <div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium opacity-90">Vehicle Owner Commission</h3><Users className="w-8 h-8 opacity-80" /></div>
                  <p className="text-3xl font-bold">{formatCurrency(totals.totalOwnerCommission)}</p>
                  <div className="mt-2 pt-2 border-t border-white/20"><div className="flex justify-between text-xs"><span>Paid: {formatCurrency(totals.totalPaidOwner)}</span><span>Pending: {formatCurrency(totals.totalPendingOwner)}</span></div></div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                  <div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium opacity-90">Primary Agent Commission</h3><Users className="w-8 h-8 opacity-80" /></div>
                  <p className="text-3xl font-bold">{formatCurrency(totals.totalCompanyCommission)}</p>
                  <div className="mt-2 pt-2 border-t border-white/20"><div className="flex justify-between text-xs"><span>Paid: {formatCurrency(totals.totalPaidCompany)}</span><span>Pending: {formatCurrency(totals.totalPendingCompany)}</span></div></div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                  <div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium opacity-90">Location Hub Commission</h3><MapPin className="w-8 h-8 opacity-80" /></div>
                  <p className="text-3xl font-bold">{formatCurrency(totals.totalHubCommission)}</p>
                  <div className="mt-2 pt-2 border-t border-white/20"><div className="flex justify-between text-xs"><span>Paid: {formatCurrency(totals.totalPaidHub)}</span><span>Pending: {formatCurrency(totals.totalPendingHub)}</span></div></div>
                </div>
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 text-white">
                  <div className="flex items-center justify-between mb-2"><h3 className="text-sm font-medium opacity-90">Total Records</h3><FileText className="w-8 h-8 opacity-80" /></div>
                  <p className="text-3xl font-bold">{filteredCommissions.length}</p>
                  <div className="mt-2 pt-2 border-t border-white/20"><div className="flex justify-between text-xs"><span>Filtered</span><span>Total Commissions</span></div></div>
                </div>
              </div>

              {/* Tiers + Info Panel */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Commission Distribution Overview</h2>
                  <div className="space-y-4">
                    {/* Owner tier */}
                    <div onClick={() => handleTierClick('owner')} className={`cursor-pointer bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] ${selectedItem === 'owner' ? 'ring-4 ring-blue-300' : ''}`}>
                      <div className="flex items-center space-x-3"><div className="bg-white/20 rounded-full p-2"><Users className="w-6 h-6" /></div><div className="flex-1"><h3 className="font-bold text-lg">Vehicle Owner Commission</h3><p className="text-xs opacity-90">Commission allocation to vehicle owner</p></div></div>
                      <div className="mt-3 pt-3 border-t border-white/20"><div className="grid grid-cols-3 gap-2 text-sm"><div><span className="opacity-90">Total:</span><p className="font-bold">{formatCurrency(totals.totalOwnerCommission)}</p></div><div><span className="opacity-90">Paid:</span><p className="font-bold">{formatCurrency(totals.totalPaidOwner)}</p></div><div><span className="opacity-90">Pending:</span><p className="font-bold">{formatCurrency(totals.totalPendingOwner)}</p></div></div></div>
                    </div>
                    {/* Company tier */}
                    <div onClick={() => handleTierClick('company')} className={`cursor-pointer bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] ${selectedItem === 'company' ? 'ring-4 ring-green-300' : ''}`}>
                      <div className="flex items-center space-x-3"><div className="bg-white/20 rounded-full p-2"><Users className="w-6 h-6" /></div><div className="flex-1"><h3 className="font-bold text-lg">Primary Agent Commission</h3><p className="text-xs opacity-90">Commission allocation to primary agent</p></div></div>
                      <div className="mt-3 pt-3 border-t border-white/20"><div className="grid grid-cols-3 gap-2 text-sm"><div><span className="opacity-90">Total:</span><p className="font-bold">{formatCurrency(totals.totalCompanyCommission)}</p></div><div><span className="opacity-90">Paid:</span><p className="font-bold">{formatCurrency(totals.totalPaidCompany)}</p></div><div><span className="opacity-90">Pending:</span><p className="font-bold">{formatCurrency(totals.totalPendingCompany)}</p></div></div></div>
                    </div>
                    {/* Hub tier */}
                    <div onClick={() => handleTierClick('hub')} className={`cursor-pointer bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-4 shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] ${selectedItem === 'hub' ? 'ring-4 ring-purple-300' : ''}`}>
                      <div className="flex items-center space-x-3"><div className="bg-white/20 rounded-full p-2"><MapPin className="w-6 h-6" /></div><div className="flex-1"><h3 className="font-bold text-lg">Location Hub Commission</h3><p className="text-xs opacity-90">Combined sender &amp; receiver hub commissions</p></div></div>
                      <div className="mt-3 pt-3 border-t border-white/20"><div className="grid grid-cols-3 gap-2 text-sm"><div><span className="opacity-90">Total:</span><p className="font-bold">{formatCurrency(totals.totalHubCommission)}</p></div><div><span className="opacity-90">Paid:</span><p className="font-bold">{formatCurrency(totals.totalPaidHub)}</p></div><div><span className="opacity-90">Pending:</span><p className="font-bold">{formatCurrency(totals.totalPendingHub)}</p></div></div></div>
                    </div>
                  </div>
                </div>

                {/* Info panel */}
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-lg shadow-md sticky top-6">
                    {renderInfoPanel()}
                  </div>
                </div>
              </div>
    </PageLayout>
  );
};

export default CommissionDashboard;
